import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Lock, Mail, Shield, Loader2 } from "lucide-react";

export const AdminLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        navigate("/admin/dashboard");
      }).catch(() => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
      });
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const loginResponse = await axios.post(`${API}/auth/login`, { email, password });
      const token = loginResponse.data.access_token;
      
      await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      localStorage.setItem("admin_token", token);
      localStorage.setItem("admin_user", JSON.stringify(loginResponse.data.user));
      
      toast.success("Welcome to Admin Dashboard");
      navigate("/admin/dashboard");
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. Admin privileges required.");
      } else {
        toast.error(error.response?.data?.detail || "Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
      <Card className="w-full max-w-md mx-4 border border-slate-700 shadow-2xl bg-slate-800" data-testid="admin-login-card">
        <CardHeader className="space-y-1 text-center pb-2">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img src="/favicon.png" alt="eSay" className="w-10 h-10" />
            <span className="text-xl font-bold text-white">eSay</span>
          </div>
          <div className="w-14 h-14 bg-red-500 rounded-xl mx-auto mb-4 flex items-center justify-center">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight text-white">
            Admin Portal
          </CardTitle>
          <CardDescription className="text-slate-400">
            eSay Administration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                type="email"
                placeholder="Admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-11 h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500"
                data-testid="admin-email-input"
                required
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-11 h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500"
                data-testid="admin-password-input"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 bg-red-500 hover:bg-red-600 text-white font-semibold shadow-lg shadow-red-500/25"
              disabled={loading}
              data-testid="admin-login-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Authenticating...
                </span>
              ) : (
                "Access Admin Portal"
              )}
            </Button>
          </form>
          <p className="text-xs text-center text-slate-500 mt-6">
            Restricted access. Authorized personnel only.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
